import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send, Bot, User as UserIcon, Plus, Trash2, Loader2, Sparkles, MessageSquare, ChevronRight, Building2 } from "lucide-react";
import api from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Header from "@/components/Header";

function RoleGate({ roles, onSaved }) {
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const handleSave = (role) => {
    const r = role || selected;
    if (!r) return;
    setSaving(true);
    api.post("/chat/role", { role: r })
      .then(() => onSaved(r))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not save role");
        setSaving(false);
      });
  };
  return (
    <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl p-8 mt-12" data-testid="chat-role-gate">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 mb-5">
        <Sparkles className="w-3 h-3 text-emerald-600" />
        <span className="text-xs font-mono uppercase tracking-[0.18em] text-emerald-700">Setup &middot; one-time</span>
      </div>
      <h2 className="font-outfit text-3xl font-bold text-slate-900">What&apos;s your role?</h2>
      <p className="mt-2 text-slate-600 text-sm">
        Tells the AI how technical to get with your answers — and feeds the community knowledge base
        we&apos;re building to show cities what kinds of questions Engineers vs PMs ask about their permits.
      </p>
      <div className="mt-6 grid grid-cols-2 gap-2">
        {roles.map((r) => (
          <button
            key={r}
            data-testid={`role-${r.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => setSelected(r)}
            className={`text-left px-4 py-3 border-2 rounded-lg text-sm transition ${
              selected === r ? "border-slate-900 bg-slate-50 font-medium" : "border-slate-200 hover:border-slate-300"
            }`}
          >
            {r}
          </button>
        ))}
      </div>
      <Button
        onClick={() => handleSave()}
        disabled={!selected || saving}
        data-testid="chat-role-save"
        className="mt-6 w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
      >
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <>Continue<ChevronRight className="w-4 h-4 ml-1" /></>}
      </Button>
      <button
        type="button"
        onClick={() => handleSave("Other")}
        disabled={saving}
        data-testid="chat-role-skip"
        className="mt-3 w-full text-center text-sm text-slate-500 hover:text-slate-900 underline underline-offset-2 transition-colors disabled:opacity-50"
      >
        Skip for now — I just want to try the AI
      </button>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-3 mb-5 ${isUser ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.role}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center flex-none mt-0.5">
          <Bot className="w-4 h-4 text-white" />
        </div>
      )}
      <div
        className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-slate-900 text-white rounded-br-sm"
            : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
        }`}
        data-testid={msg.streaming ? "msg-assistant-streaming" : undefined}
      >
        {msg.content}
        {msg.streaming && (
          <span
            className="inline-block w-[2px] h-4 ml-0.5 bg-indigo-500 align-middle animate-pulse"
            aria-hidden="true"
          />
        )}
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center flex-none mt-0.5">
          <UserIcon className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  );
}

export default function Chat() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [needsRole, setNeedsRole] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [roles, setRoles] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [jurisdiction, setJurisdiction] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/chat/roles").then((r) => setRoles(r.data.roles));
    api.get("/chat/me").then((r) => {
      setNeedsRole(r.data.needs_role);
      setUserRole(r.data.construction_role);
    });
  }, []);

  const loadSessions = useCallback(() => {
    api.get("/chat/sessions").then((r) => setSessions(r.data.sessions));
  }, []);

  useEffect(() => {
    if (needsRole === false) loadSessions();
  }, [needsRole, loadSessions]);

  const openSession = (sid) => {
    setActiveId(sid);
    setMessages([]);
    api.get(`/chat/sessions/${sid}`).then((r) => setMessages(r.data.messages));
  };

  const newSession = () => {
    setActiveId(null);
    setMessages([]);
    setInput("");
  };

  const handleSend = () => {
    const q = input.trim();
    if (!q) return;
    setMessages((m) => [...m, { role: "user", content: q, id: Date.now() }]);
    setInput("");
    setSending(true);

    // Open SSE stream — EventSource can't set headers so the JWT is in the query string.
    const backendBase = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
    const token = localStorage.getItem("token") || "";
    const params = new URLSearchParams({ question: q, token });
    if (activeId) params.set("session_id", activeId);
    if (jurisdiction) params.set("jurisdiction", jurisdiction);
    const url = `${backendBase}/api/chat/stream?${params.toString()}`;
    const es = new EventSource(url);

    // Streaming assistant bubble — appended once we get the first delta.
    const streamMsgId = Date.now() + 1;
    let bufferedContent = "";
    let assistantAppended = false;

    es.addEventListener("meta", (ev) => {
      try {
        const m = JSON.parse(ev.data);
        if (m.session_id && !activeId) setActiveId(m.session_id);
      } catch (_e) { /* ignore */ }
    });

    es.addEventListener("delta", (ev) => {
      try {
        const m = JSON.parse(ev.data);
        bufferedContent += m.content || "";
        if (!assistantAppended) {
          assistantAppended = true;
          setMessages((prev) => [...prev, {
            role: "assistant", content: bufferedContent, id: streamMsgId, streaming: true,
          }]);
        } else {
          setMessages((prev) => prev.map(x => x.id === streamMsgId
            ? { ...x, content: bufferedContent } : x));
        }
      } catch (_e) { /* ignore */ }
    });

    es.addEventListener("done", (ev) => {
      try {
        const m = JSON.parse(ev.data);
        setMessages((prev) => prev.map(x => x.id === streamMsgId
          ? { ...x, content: m.answer || bufferedContent, streaming: false } : x));
        if (m.session_id && !activeId) setActiveId(m.session_id);
      } catch (_e) { /* ignore */ }
      setSending(false);
      loadSessions();
      es.close();
    });

    es.addEventListener("error", (ev) => {
      let detail = "AI stream interrupted — please try again.";
      try {
        if (ev?.data) {
          const m = JSON.parse(ev.data);
          if (m.detail) detail = m.detail;
        }
      } catch (_e) { /* network-level error has no payload */ }
      toast.error(detail);
      setMessages((prev) => prev.filter(x => x.id !== streamMsgId));
      setSending(false);
      es.close();
    });
  };

  const handleDelete = (sid) => {
    if (!window.confirm("Delete this chat?")) return;
    api.delete(`/chat/sessions/${sid}`).then(() => {
      if (sid === activeId) newSession();
      loadSessions();
    });
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="max-w-md mx-auto mt-24 p-8 text-center">
          <Bot className="w-10 h-10 text-slate-300 mx-auto mb-4" />
          <h2 className="font-outfit text-2xl font-bold text-slate-900">Sign in to chat</h2>
          <p className="mt-2 text-slate-600">The AI assistant is available to all Coast To Coast Permitting members (free trial included).</p>
          <Button asChild className="mt-5 bg-slate-900 hover:bg-slate-800 text-white">
            <Link to="/login">Sign in</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (needsRole === null) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]"><Header /><div className="text-center mt-24 text-slate-500">Loading…</div></div>
    );
  }

  if (needsRole) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="chat-page">
        <Header />
        <RoleGate roles={roles} onSaved={(r) => { setNeedsRole(false); setUserRole(r); }} />
      </div>
    );
  }

  const suggestions = [
    "What permits do I need for a residential ADU in Los Angeles County?",
    "Typical plan review turnaround in Maricopa County?",
    "What triggers a special inspection under IBC 1704?",
    "Common reasons electrical permits get rejected on first submittal?",
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="chat-page">
      <Header />
      <main className="max-w-screen-2xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 min-h-[calc(100vh-180px)]">
          {/* Sidebar */}
          <aside className="bg-white border border-slate-200 rounded-2xl p-4 h-fit">
            <Button onClick={newSession} className="w-full bg-slate-900 hover:bg-slate-800 text-white mb-4" data-testid="chat-new-btn">
              <Plus className="w-4 h-4 mr-2" /> New chat
            </Button>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2 px-1">Role</div>
            <Badge variant="outline" className="mb-4 font-mono text-[10px]" data-testid="chat-role-badge">
              <Building2 className="w-3 h-3 mr-1" />{userRole}
            </Badge>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2 px-1">Past chats</div>
            {sessions.length === 0 ? (
              <p className="text-xs text-slate-400 px-1 py-2">No chats yet</p>
            ) : (
              <ul className="space-y-1 max-h-[60vh] overflow-y-auto">
                {sessions.map((s) => (
                  <li key={s.session_id} className="group flex items-center gap-1">
                    <button
                      onClick={() => openSession(s.session_id)}
                      data-testid={`session-${s.session_id}`}
                      className={`flex-1 min-w-0 text-left px-2 py-1.5 rounded text-xs truncate ${
                        s.session_id === activeId ? "bg-slate-100 text-slate-900 font-medium" : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {s.title}
                    </button>
                    <button
                      onClick={() => handleDelete(s.session_id)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded text-red-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Main panel */}
          <section className="bg-white border border-slate-200 rounded-2xl flex flex-col" data-testid="chat-panel">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-700 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-outfit text-lg font-bold text-slate-900">Coast To Coast Permitting · AI</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Powered by Claude Haiku 4.5 · Streaming</div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 bg-[#FAFBFD]" data-testid="chat-scroll">
              {messages.length === 0 ? (
                <div className="max-w-xl mx-auto text-center py-8">
                  <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-outfit text-2xl font-bold text-slate-900">Ask anything about permits.</h3>
                  <p className="mt-2 text-slate-500 text-sm">
                    Plan review, fee schedules, code interpretation, AHJ workflows across U.S. Cities, Counties, Townships and Metros.
                  </p>
                  <div className="mt-6 grid gap-2">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => setInput(s)}
                        data-testid="chat-suggest"
                        className="text-left px-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-700 hover:border-slate-400 hover:bg-white transition"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((m) => <MessageBubble key={m.id || m.created_at} msg={m} />)
              )}
              {sending && !messages.some(m => m.streaming) && (
                <div className="flex items-center gap-2 text-slate-400 text-sm" data-testid="chat-thinking">
                  <Loader2 className="w-4 h-4 animate-spin" /> Thinking…
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200">
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={jurisdiction}
                  onChange={(e) => setJurisdiction(e.target.value)}
                  placeholder="Jurisdiction context (optional, e.g. 'Maricopa County AZ')"
                  data-testid="chat-jurisdiction-input"
                  className="flex-1 px-3 py-1.5 border border-slate-200 rounded-md text-xs bg-slate-50 focus:bg-white focus:outline-none focus:border-slate-400"
                />
              </div>
              <div className="flex gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Ask anything about permits, fees, codes, AHJ workflows…"
                  data-testid="chat-input"
                  rows={2}
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-sm resize-none focus:outline-none focus:border-slate-500"
                />
                <Button
                  onClick={handleSend}
                  disabled={sending || !input.trim()}
                  data-testid="chat-send"
                  className="bg-slate-900 hover:bg-slate-800 text-white px-6 self-stretch"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="mt-2 text-[10px] text-slate-400">
                AI can be wrong — always verify code citations with your AHJ. Conversations are stored to improve the community knowledge base.
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
